import { mediaUrl } from "@/lib/helpers";
import { Link } from "react-router-dom";
import { Calendar, MapPin } from "lucide-react";
import { VerifiedBadge, Flag, CategoryLabel } from "./Badges";
import { fmtDate } from "@/lib/helpers";

export function ProfileCard({ p, className = "" }) {
  return (
    <Link to={`/registry/${p.slug}`} data-testid={`profile-card-${p.slug}`} className={`wei-card wei-card-hover overflow-hidden block group ${className}`}>
      <div className="aspect-[4/3] overflow-hidden bg-stone img-zoom">
        <img src={mediaUrl(p.image)} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="p-3.5">
        <div className="flex items-center gap-1.5">
          <h3 className="font-semibold text-[13px] tracking-[0.06em] uppercase text-ink truncate">{p.name}</h3>
          <VerifiedBadge status={p.verification_status} compact />
        </div>
        <p className="text-xs text-charcoal mt-1 truncate">{(p.professions || []).slice(0, 2).join(" · ")}</p>
        <Flag code={p.country_code} name={p.country} className="mt-1.5" />
      </div>
    </Link>
  );
}

export function EventCard({ e, light = true }) {
  const d = new Date(e.date + "T00:00:00");
  return (
    <Link to={`/events/${e.slug}`} data-testid={`event-card-${e.slug}`} className="wei-card wei-card-hover overflow-hidden block group">
      <div className="aspect-[16/10] overflow-hidden bg-stone img-zoom">
        <img src={mediaUrl(e.image)} alt={e.title} className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="flex">
        <div className="w-16 shrink-0 flex flex-col items-center justify-center border-r border-[#EFEDE8] py-3">
          <span className="font-serif text-2xl font-semibold text-ink leading-none">{d.getDate()}</span>
          <span className="text-[10px] uppercase tracking-widest text-charcoal mt-1">{d.toLocaleString("en-GB", { month: "short" })}</span>
        </div>
        <div className="p-3 min-w-0">
          <h3 className="font-semibold text-[12px] uppercase tracking-[0.06em] text-ink truncate">{e.title}</h3>
          <p className="text-[11px] text-charcoal mt-0.5">{e.category}</p>
          <p className="text-[11px] text-[#6B675F] mt-1 inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {e.city}, {e.country}</p>
        </div>
      </div>
    </Link>
  );
}

export function ArticleCard({ a, large = false }) {
  if (large) {
    return (
      <Link to={`/news/${a.slug}`} data-testid={`article-card-${a.slug}`} className="relative block overflow-hidden rounded-md bg-ink group min-h-[380px] h-full">
        <img src={mediaUrl(a.image)} alt={a.title} className="absolute inset-0 w-full h-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-[1.03]" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-transparent" />
        <div className="absolute bottom-0 p-6 sm:p-8 text-white">
          <span className="inline-block bg-bordeaux text-white text-[10px] font-semibold tracking-[0.18em] px-2 py-1 rounded-sm mb-3">{a.category}</span>
          <h3 className="font-serif text-2xl sm:text-3xl font-semibold leading-tight max-w-lg">{a.title}</h3>
          <p className="text-sm text-white/75 mt-3 max-w-md">{a.excerpt}</p>
          <p className="text-xs text-white/60 mt-4 inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {fmtDate(a.date)}</p>
        </div>
      </Link>
    );
  }
  return (
    <Link to={`/news/${a.slug}`} data-testid={`article-card-${a.slug}`} className="flex gap-4 group">
      <div className="w-24 h-20 shrink-0 overflow-hidden rounded-sm bg-stone img-zoom"><img src={mediaUrl(a.image)} alt="" className="w-full h-full object-cover" loading="lazy" /></div>
      <div className="min-w-0">
        <CategoryLabel>{a.category}</CategoryLabel>
        <h3 className="font-semibold text-sm text-ink leading-snug mt-1 group-hover:text-bordeaux transition-colors">{a.title}</h3>
        <p className="text-[11px] text-[#6B675F] mt-1.5 inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {fmtDate(a.date)}</p>
      </div>
    </Link>
  );
}
